import React, { FC, useEffect, useRef, useState } from 'react';
import { VisuallyHidden } from '@chakra-ui/react';
import { i18n } from '../../i18n/translate';
import { getDisplayPageTitle, getPageTitleFromDocumentTitle } from '../../metatags/title';

type FocusMode = 'none' | 'title' | 'main' | 'title-or-main';

type PageLoadAnnouncerProps = {
  /**
   * Controls whether route changes move keyboard focus.
   * Prefer "title" when page components register their visible page heading.
   */
  focusMode?: FocusMode;
  /**
   * Fallback focus selector used by "main" and "title-or-main" modes.
   */
  mainSelector?: string;
  /**
   * Selector for the visible page title element that should receive focus.
   */
  titleSelector?: string;
};

const ANNOUNCEMENT_DELAY = 50;
const DUPLICATE_ANNOUNCEMENT_DELAY = 500;
const FOCUS_DELAY = 1000;

const isHTMLElement = (value: unknown): value is HTMLElement => {
  return typeof HTMLElement !== 'undefined' && value instanceof HTMLElement;
};

const canReceiveFocus = (element: HTMLElement) => {
  return element.isConnected && element.getAttribute('aria-hidden') !== 'true';
};

const getMainTarget = (selector: string) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const target = document.querySelector(selector);

  return isHTMLElement(target) ? target : null;
};

const getTitleTarget = (selector: string) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const target = document.querySelector(selector);

  return isHTMLElement(target) ? target : null;
};

const focusElement = (element: HTMLElement) => {
  if (!canReceiveFocus(element)) {
    return;
  }

  const previousTabIndex = element.getAttribute('tabindex');
  element.setAttribute('tabindex', '-1');
  element.focus({ preventScroll: true });
  element.scrollIntoView({ block: 'start' });

  const restoreTabIndex = () => {
    element.removeEventListener('blur', restoreTabIndex);
    if (previousTabIndex === null) {
      element.removeAttribute('tabindex');
    } else {
      element.setAttribute('tabindex', previousTabIndex);
    }
  };

  element.addEventListener('blur', restoreTabIndex);
};

const getFocusTarget = (focusMode: FocusMode, titleSelector: string, mainSelector: string) => {
  if (focusMode === 'none') {
    return null;
  }

  const titleTarget = getTitleTarget(titleSelector);

  if ((focusMode === 'title' || focusMode === 'title-or-main') && titleTarget) {
    return titleTarget;
  }

  if (focusMode === 'main' || focusMode === 'title-or-main') {
    return getMainTarget(mainSelector);
  }

  return null;
};

const getAnnouncerText = (title: string) => {
  return i18n.t('New page %s', { postProcess: 'sprintf', sprintf: [title] });
};

export const PageLoadAnnouncer: FC<PageLoadAnnouncerProps> = ({
  focusMode = 'none',
  mainSelector = 'main',
  titleSelector = '[data-page-title="true"]',
}) => {
  const [announcement, setAnnouncement] = useState('');
  const announcementTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastAnnouncedTitleRef = useRef('');
  const lastAnnouncedAtRef = useRef(0);
  const lastDocumentTitleRef = useRef(typeof document === 'undefined' ? '' : document.title);

  useEffect(() => {
    const clearTimers = () => {
      if (announcementTimeoutRef.current !== undefined) {
        clearTimeout(announcementTimeoutRef.current);
        announcementTimeoutRef.current = undefined;
      }

      if (focusTimeoutRef.current !== undefined) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = undefined;
      }
    };

    const announcePage = (title: string) => {
      const displayTitle = getDisplayPageTitle(title);

      const now = Date.now();
      const isDuplicateTitle = displayTitle === lastAnnouncedTitleRef.current;
      const isImmediateRepeat = now - lastAnnouncedAtRef.current < DUPLICATE_ANNOUNCEMENT_DELAY;

      if (!displayTitle || (isDuplicateTitle && isImmediateRepeat)) {
        return;
      }

      clearTimers();
      lastAnnouncedTitleRef.current = displayTitle;
      lastAnnouncedAtRef.current = now;
      setAnnouncement('');

      announcementTimeoutRef.current = setTimeout(() => {
        setAnnouncement(getAnnouncerText(displayTitle));
        announcementTimeoutRef.current = undefined;
      }, ANNOUNCEMENT_DELAY);

      if (focusMode !== 'none') {
        focusTimeoutRef.current = setTimeout(() => {
          const target = getFocusTarget(focusMode, titleSelector, mainSelector);

          if (target) {
            focusElement(target);
          }

          focusTimeoutRef.current = undefined;
        }, FOCUS_DELAY);
      }
    };

    const announceDocumentTitle = () => {
      if (typeof document === 'undefined' || document.title === lastDocumentTitleRef.current) {
        return;
      }

      lastDocumentTitleRef.current = document.title;
      announcePage(getPageTitleFromDocumentTitle(document.title));
    };

    const titleElement = document.querySelector('title');
    let titleObserver: MutationObserver | null = null;

    if (titleElement) {
      titleObserver = new MutationObserver(announceDocumentTitle);
      titleObserver.observe(titleElement, { characterData: true, childList: true, subtree: true });
    }

    return () => {
      clearTimers();
      titleObserver?.disconnect();
    };
  }, [focusMode, mainSelector, titleSelector]);

  return (
    <VisuallyHidden aria-atomic="true" aria-live="polite" as="div" role="status">
      {announcement}
    </VisuallyHidden>
  );
};
